import React from "react";
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
} from "@mui/material";
import CustomerDropdown from "../../components/CustomerDropdown";
import ContactAutocompleteWithAddNew from "../../components/ContactAutocompleteWithAddNew";

/* ------------------------------------------------------------------ */
/* Options                                                             */
/* ------------------------------------------------------------------ */
const TITLE_OPTIONS = [
  { value: "", label: "Select" },
  "Mr.",
  "Ms.",
  "Mrs.",
  "Dr.",
];

const PAYMENT_MODE_OPTIONS = [
  { value: "", label: "Select payment mode" },
  "Postpaid",
  "COD",
];

const ACCOUNTING_TYPE_OPTIONS = [
  { value: "Confirmed", label: "Confirmed" },
  { value: "Estimation", label: "Estimation" },
];

/* ------------------------------------------------------------------ */
/* Design tokens (same as the booking form)                            */
/* ------------------------------------------------------------------ */
const FIELD_H = 40; // one height for every input, select and dropdown

const C = {
  primary: "#1d4ed8",
  surface: "#f1f5f9",
  border: "#94a3b8",        // was #cbd5e1 (darker field borders)
  borderStrong: "#64748b",  // was #94a3b8
  text: "#0f172a",          // was #151d29
  muted: "#64748b",         // was #94a3b8 (labels / helper text)
};

const controlSx = {
  minWidth: 0,
  width: "100%",

  "& .MuiFormControl-root, & .MuiAutocomplete-root": {
    width: "100%",
  },

  "& .MuiInputBase-root:not(.MuiInputBase-multiline), & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root":
    {
      height: FIELD_H,
      boxSizing: "border-box",
      fontSize: 15,
      fontWeight: 600,
      color: C.text,
      backgroundColor: "#fff",
      borderRadius: "8px",
      paddingTop: 0,
      paddingBottom: 0,
    },

  "& .MuiInputBase-input:not(.MuiInputBase-inputMultiline)": {
    height: "100%",
    boxSizing: "border-box",
    padding: "0 12px",
    fontSize: 15,
    fontWeight: 600,
    color: "#0f172a",
    WebkitTextFillColor: "#0f172a",
  },

  "& .MuiSelect-select.MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    boxSizing: "border-box",
    padding: "0 32px 0 12px",
    minHeight: 0,
    fontSize: 15,
    fontWeight: 600,
    color: C.text,
  },

  "& .MuiAutocomplete-inputRoot": {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "0 !important",
    flexWrap: "nowrap",
  },

  "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
    height: "100%",
    padding: "0 12px !important",
    fontSize: "15px !important",
    fontWeight: 600,
    color: `${C.text} !important`,
    WebkitTextFillColor: `${C.text} !important`,
  },

  "& .MuiAutocomplete-endAdornment": {
    right: 6,
  },

  "& .MuiInputLabel-root": {
    fontSize: 14,
    fontWeight: 500,
    color: C.muted,
    transform: "translate(13px, 12px) scale(1)",
  },

  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    fontSize: 14,
    fontWeight: 500,
    color: "#94a3b8",
    transform: "translate(13px, -7px) scale(0.85)",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: C.primary,
    fontWeight: 500,
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: C.border,
  },

  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: C.borderStrong,
  },

  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: C.primary,
    borderWidth: 1,
  },

  "& .MuiOutlinedInput-root.Mui-focused": {
    boxShadow: "0 0 0 3px rgba(29,78,216,0.12)",
  },

  "& .MuiFormHelperText-root": {
    fontSize: 12,
    color: C.muted,
    mt: 0.25,
    ml: 0.5,
  },

  "& .MuiSvgIcon-root": {
    fontSize: 18,
  },
};

const readOnlySx = {
  "& .MuiInputBase-root": { backgroundColor: `${C.surface} !important` },
  "& .MuiInputBase-input": {
    color: `${C.text} !important`,
    WebkitTextFillColor: `${C.text} !important`,
  },
};

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */
const Card = ({ title, subtitle, children }) => (
  <Box
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "12px",
      backgroundColor: "#fff",
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        px: 2.5,
        py: 1.25,
        minHeight: 56,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#64748b" }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 11, fontWeight: 400, color: C.muted, mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

const Grid = ({ cols = 4, children, sx }) => (
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: `repeat(${cols}, 1fr)`,
      },
      alignItems: "start",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Column span helper for the responsive grid
const span = (n) => ({ gridColumn: { sm: "span 2", md: `span ${n}` } });

const SubLabel = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 3, mb: 2 }}>
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 500,
        color: "#64748b",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "1px", backgroundColor: C.border }} />
  </Box>
);

// Wrapper for external dropdown / autocomplete components
const Slot = ({ children, sx }) => (
  <Box sx={{ ...controlSx, ...sx }}>{children}</Box>
);

// Text input / select with consistent styling
const Field = ({ options, sx, InputProps, ...props }) => {
  const locked = !!InputProps?.readOnly || !!props.disabled;
  return (
    <TextField
      size="small"
      fullWidth
      select={!!options}
      InputProps={InputProps}
      sx={{ ...controlSx, ...(locked ? readOnlySx : {}), ...sx }}
      {...props}
    >
      {options &&
        options.map((o) => {
          const opt = typeof o === "string" ? { value: o, label: o } : o;
          return (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{
                fontSize: 13,
                fontWeight: 400,
                color: C.text,
              }}
            >
              {opt.label}
            </MenuItem>
          );
        })}
    </TextField>
  );
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function TripsheetHeader({
  form,
  handleFormChange,
  dropdownData,
  clientId,
  onOrderByNameChange, // optional callback from parent
  onCustomerChange,
}) {
  // Called while the user TYPES in the Order by box.
  // With fieldPrefix="orderby" the autocomplete sends "orderby_name",
  // which matches the field this form actually stores and displays.
  const handleOrderByFormChange = (e) => {
    const { name } = e.target;
    if (name === "orderby_name") {
      handleFormChange(e);

      // A hand-typed name no longer matches the previously selected contact
      if (form.orderby_contact) {
        handleFormChange({ target: { name: "orderby_contact", value: null } });
      }
    }
  };

  // Customer dropdown change
  const handleCustomerChange = (val) => {
    handleFormChange({ target: { name: "customerid", value: val } });

    const selectedCust = (dropdownData?.customers || []).find(
      (c) => String(c.customerid) === String(val),
    );
    const customerName = selectedCust ? selectedCust.customername : "";

    if (typeof onCustomerChange === "function") {
      onCustomerChange(customerName);
    }
  };

  // Used when a contact is selected, added, or cleared.
  // `ContactAutocompleteWithAddNew` calls onValueChange(event, newValue)
  const handleOrderBySelection = (event, value) => {
    if (!value) {
      // cleared
      handleFormChange({ target: { name: "orderbyid", value: null } });
      handleFormChange({ target: { name: "orderby_name", value: "" } });
      handleFormChange({ target: { name: "orderby_mobile", value: "" } });
      handleFormChange({ target: { name: "orderby_email", value: "" } });
      handleFormChange({ target: { name: "orderby_department", value: "" } });
      handleFormChange({ target: { name: "orderby_title", value: "" } });
      handleFormChange({ target: { name: "orderby_contact", value: null } });
      if (typeof onOrderByNameChange === "function") onOrderByNameChange(null);
      return;
    }

    handleFormChange({
      target: {
        name: "orderbyid",
        value: value.contactid || value.customercontactid || null,
      },
    });

    const fullName =
      `${value.customercontactfirstname || ""} ${value.customercontactlastname || ""}`.trim();
    handleFormChange({ target: { name: "orderby_name", value: fullName } });
    handleFormChange({
      target: {
        name: "orderby_mobile",
        value: value.customercontactmobile || "",
      },
    });
    handleFormChange({
      target: {
        name: "orderby_email",
        value: value.customercontactemail || "",
      },
    });
    handleFormChange({
      target: {
        name: "orderby_department",
        value: value.customercontactdepartment || "",
      },
    });
    handleFormChange({
      target: {
        name: "orderby_title",
        value:
          value.customercontactsalutation || value.customercontacttitle || "",
      },
    });
    handleFormChange({ target: { name: "orderby_contact", value } });

    if (typeof onOrderByNameChange === "function") onOrderByNameChange(value);
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* <Card title=""> */}
        <Grid cols={4}>
          <Field
            label="Tripsheet number"
            name="tripsheet_number"
            value={form.tripsheet_number || ""}
            onChange={handleFormChange}
            disabled={Boolean(form.tripsheet_number)}
          />
          <Field
            label="Reference"
            name="reference"
            value={form.reference || ""}
            onChange={handleFormChange}
          />
          <Field
            label="Booking type"
            name="accounting_type"
            value={form.accounting_type || "Confirmed"}
            onChange={handleFormChange}
            options={ACCOUNTING_TYPE_OPTIONS}
          />
          <Field
            label="Payment mode"
            name="payment_mode"
            value={form.payment_mode || ""}
            onChange={handleFormChange}
            options={PAYMENT_MODE_OPTIONS}
          />

          <Slot sx={span(3)}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Customer</InputLabel>
              <CustomerDropdown
                customers={dropdownData?.customers || []}
                selectedCustomerId={form.customerid}
                onChange={handleCustomerChange}
              />
            </FormControl>
          </Slot>
          <Field
            label="P.O. number"
            name="pono"
            value={form.pono || ""}
            onChange={handleFormChange}
          />
        </Grid>

        <SubLabel>Order by (booking requester)</SubLabel>
        <Grid cols={4}>
          <Field
            label="Title"
            name="orderby_title"
            value={form.orderby_title || ""}
            onChange={handleFormChange}
            options={TITLE_OPTIONS}
          />
          <Slot>
            <ContactAutocompleteWithAddNew
              label="Order by"
              fieldPrefix="orderby" // matches orderby_name, orderby_title, ...
              contactType="Orderby"
              form={form}
              contacts={dropdownData?.orderbyContacts || []}
              formValue={form.orderby_name || ""} // string shown in input
              onFormChange={handleOrderByFormChange} // typing updates orderby_name
              onValueChange={handleOrderBySelection} // selection fills name/mobile/email/...
              clientId={clientId}
              customerId={form.customerid}
            />
          </Slot>
          <Field
            label="Mobile"
            value={form.orderby_mobile || ""}
            InputProps={{ readOnly: true }}
          />
          <Field
            label="Email"
            value={form.orderby_email || ""}
            InputProps={{ readOnly: true }}
          />
        </Grid>
      {/* </Card> */}
    </Box>
  );
}